// ---------------------------------------------------------------------------
// Backend API client
//
// Answer validation is server-authoritative: the client never holds the answer
// key. `checkAnswer` and `fiftyFifty` resolve correctness against the backend,
// which is the single source of truth for scoring.
// ---------------------------------------------------------------------------

// Strip any trailing slash so a value like "https://host.com/" doesn't produce
// a double slash ("https://host.com//api/...") that fails to match Express routes.
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

export interface CheckAnswerResult {
  correct: boolean;
  correctAnswer: number;
}

export interface FiftyFiftyResult {
  hide: number[];
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    // Send/receive the session cookie so SIWE-authenticated routes work.
    credentials: 'include',
    ...init,
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch {
      // non-JSON error body — keep the generic message
    }
    const error = new Error(message) as Error & { status?: number };
    error.status = res.status;
    throw error;
  }

  return res.json() as Promise<T>;
}

function getJson<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' });
}

function postJson<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

function putJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function checkAnswer(questionId: number, answerIndex: number): Promise<CheckAnswerResult> {
  return postJson<CheckAnswerResult>('/api/check-answer', { questionId, answerIndex });
}

export function fiftyFifty(questionId: number): Promise<FiftyFiftyResult> {
  return postJson<FiftyFiftyResult>('/api/fifty-fifty', { questionId });
}

// ---------------------------------------------------------------------------
// SIWE authentication
// ---------------------------------------------------------------------------

export interface SessionResult {
  authenticated: boolean;
  address?: string;
}

export function getNonce(): Promise<{ nonce: string }> {
  return getJson<{ nonce: string }>('/api/auth/nonce');
}

export function verifySiwe(message: string, signature: string): Promise<{ success: boolean; address: string }> {
  return postJson<{ success: boolean; address: string }>('/api/auth/verify', { message, signature });
}

export function getSession(): Promise<SessionResult> {
  return getJson<SessionResult>('/api/auth/session');
}

export function signOut(): Promise<{ success: boolean }> {
  return postJson<{ success: boolean }>('/api/auth/signout');
}

// ---------------------------------------------------------------------------
// Achievement persistence (wallet-keyed, requires an authenticated session)
// ---------------------------------------------------------------------------

export interface AchievementRecord {
  achievements: { id: string; unlockedAt: number }[];
  totalPoints: number;
}

export function getMyAchievements(): Promise<AchievementRecord> {
  return getJson<AchievementRecord>('/api/achievements/me');
}

export function putMyAchievements(record: AchievementRecord): Promise<AchievementRecord> {
  return putJson<AchievementRecord>('/api/achievements/me', record);
}

// ---------------------------------------------------------------------------
// Leaderboard (real player rankings)
// ---------------------------------------------------------------------------

export type LeaderboardPeriod = 'daily' | 'weekly' | 'alltime';

export interface LeaderboardEntry {
  rank: number;
  address: string;
  // ENS name when resolvable, otherwise a truncated address — ready to display.
  displayName: string;
  correct: number;
  total: number;
  accuracy: number;
  questionsAnswered: number;
}

export function getLeaderboard(period: LeaderboardPeriod, limit: number): Promise<LeaderboardEntry[]> {
  return getJson<LeaderboardEntry[]>(`/api/leaderboard?period=${period}&limit=${limit}`);
}

// Record a finished game for the authenticated wallet. Best-effort: the server
// derives identity from the SIWE session, so callers just send the tally.
export function submitScore(payload: {
  correct: number;
  questionsAnswered: number;
  total: number;
}): Promise<{ success: boolean }> {
  return postJson<{ success: boolean }>('/api/leaderboard/score', payload);
}
